import { type ReactNode, useEffect, useState } from 'react'

import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/accordion/accordion'
import { LoadingSpinner } from '@/components/loading-spinner/loading-spinner'

import { cn } from '@/lib/utils'

export interface AccordionMenuProps {
  menuData: {
    title: string
    count: number
    content: ReactNode
  }[]
  isFetchingData: boolean
  expandedMenuNames?: string[]
}

export const AccordionMenu = ({ menuData, expandedMenuNames, isFetchingData }: AccordionMenuProps) => {
  const [accordionValue, setAccordionValue] = useState<string[]>([])

  const handleChangeValue = (value: string[]) => {
    setAccordionValue(value)
  }

  useEffect(() => {
    expandedMenuNames && expandedMenuNames.length > 0
      ? setAccordionValue((prev) => [...prev, ...expandedMenuNames])
      : setAccordionValue((prev) => prev.filter((v) => !expandedMenuNames?.includes(v)))
  }, [expandedMenuNames])

  return (
    <Accordion type="multiple" value={accordionValue} onValueChange={handleChangeValue}>
      {menuData.map(({ title, count, content }) => (
        <AccordionItem key={title} value={title}>
          <AccordionTrigger chevronPosition="left" hoverEffect="background">
            <AccordionTriggerText title={title} count={count} loading={isFetchingData} />
          </AccordionTrigger>
          <AccordionContent>
            <div className={cn('pl-7 pt-2')}>{content}</div>
          </AccordionContent>
        </AccordionItem>
      ))}
    </Accordion>
  )
}

const AccordionTriggerText = ({ title, count, loading }: { title: string; count: number; loading: boolean }) => {
  return (
    <span className={cn('block', 'flex items-center gap-x-2.5')}>
      <span className={cn('text-lg font-semibold text-foreground')}>{title}</span>
      <span
        className={cn(
          'inline-flex items-center',
          'h-5',
          'px-2',
          'text-sm font-medium text-blue-900',
          'bg-blue-100',
          'rounded-[5px]'
        )}
      >
        {loading && <LoadingSpinner iconSize={16} />}
        {!loading && count}
      </span>
    </span>
  )
}
